import { useMemo, memo } from "react"
import { ItimeSlot } from "types/ticket"
import useWindowSize from "hooks/useWindowSize"
import SelectionCard from "../SelectionCard"
import { useCartDispatch } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import { useBookingV2 } from "context/BookingV2Context"
import StepPlaceholderMessage from "../StepPlaceholderMessage"
import { useLang } from "hooks/useLang"
import FadeOutEffect from "components/atoms/FadeOutEffect"
import {
    isTimeslotDisabled,
    getTimeslotDateKey,
} from "utils/helpers/bookingv2/ticket"
import dayjs from "dayjs"

const SelectDay = () => {
    const { isTabletOrSmaller } = useWindowSize()
    const cartDispatch = useCartDispatch()
    const { tickets, event, selectedDateKey, setSelectedDateKey } =
        useBookingV2()
    const { t, isAr, lang } = useLang()

    const selectedDayKey = selectedDateKey

    // Handle selecting a day: set date key for multi-slot days, or add to cart for single-slot days
    const handleDayClick = (dayKey: string, daySlots: ItimeSlot[]) => {
        const enabledSlots = daySlots.filter((s) => !isTimeslotDisabled(s))
        if (enabledSlots.length === 0) return

        setSelectedDateKey(dayKey) // This triggers the availableSteps recalculation

        if (enabledSlots.length > 1) {
            // For multi-slot days, add a minimal day entry to enable next step
            const firstSlot = enabledSlots[0]
            const currentDate = dayjs(firstSlot.start_slot)

            // Create a minimal day entry that won't skip SelectTime step
            const minimalDay = {
                timeslot_id: "", // Empty string to indicate placeholder for multi-slot selection
                dayName: currentDate.format("dddd"),
                dayNumber: currentDate.date(),
                monthName: currentDate.format("MMMM"),
                monthNum: currentDate.month() + 1,
                yearNum: currentDate.year().toString(),
                start: new Date(firstSlot.start_slot),
                end: new Date(firstSlot.end_slot),
                types: [], // Empty types, will be filled by SelectTime
                seats_season_id: firstSlot.seats_season_id || event.seatsio?.season_id || "",
                allow_using_coupon: false,
            }

            cartDispatch({
                type: ImprovedCartActionsEnum.ADD_DAY,
                payload: minimalDay,
            })
            return
        }

        // Single slot for this day: add directly, but do NOT auto-advance.
        const singleTimeSlot = enabledSlots[0]

        const currentDate = dayjs(singleTimeSlot.start_slot)
        const defaultDay = {
            timeslot_id: singleTimeSlot._id,
            dayName: currentDate.format("dddd"),
            dayNumber: currentDate.date(),
            monthName: currentDate.format("MMMM"),
            monthNum: currentDate.month() + 1,
            yearNum: currentDate.year().toString(),
            start: new Date(singleTimeSlot.start_slot),
            end: new Date(singleTimeSlot.end_slot),
            types: singleTimeSlot.types.map((type) => {
                const id =
                    typeof type.type_id === "string"
                        ? type.type_id
                        : //@ts-ignore
                          type.type_id?._id || ""

                const ticketDetails = event.availableTickets.find(
                    (t) => t._id === id
                )
                return {
                    _id: id,
                    type_id: id,
                    ar_description: ticketDetails?.ar_description ?? "",
                    en_description: ticketDetails?.en_description ?? "",
                    en_title: ticketDetails?.en_title ?? "",
                    ar_title: ticketDetails?.ar_title ?? "",
                    color: ticketDetails?.color ?? "#ffffff",
                    discount: ticketDetails?.discount ?? {
                        current_value: 0,
                        previous_value: 0,
                    },
                    limit: type.limit,
                    max: ticketDetails?.max ?? 0,
                    min: ticketDetails?.min ?? 1,
                    must_select_seat: ticketDetails?.must_select_seat ?? false,
                    price: ticketDetails?.price ?? 0,
                    group: (ticketDetails as any)?.group,
                    wait_list: type.wait_list,
                    promotion: ticketDetails?.promotion,
                }
            }),
            seats_season_id: singleTimeSlot.seats_season_id || event.seatsio?.season_id || "",
            allow_using_coupon: singleTimeSlot.allow_using_coupon,
        }

        cartDispatch({
            type: ImprovedCartActionsEnum.ADD_DAY,
            payload: defaultDay,
        })
    }

    const getTruncatedName = (name: string) => {
        return isTabletOrSmaller && !isAr ? name.slice(0, 3) : name
    }

    // Group timeslots by Year -> Month -> DayKey (start_end composite). Only one card per dayKey.
    // Timeslots are grouped only when they share the same start AND end date.
    const groupedDays = useMemo(() => {
        type MonthGroup = {
            month: string
            days: {
                key: string
                labelDay: string
                labelDate: string
                slots: ItimeSlot[]
                isSoldOut: boolean
                hasWaitlist: boolean
            }[]
        }
        type YearGroup = { year: string; months: MonthGroup[] }

        const years: Record<
            string,
            Record<string, Record<string, ItimeSlot[]>>
        > = {}

        tickets.forEach((slot) => {
            const d = new Date(slot.start_slot)
            const year = String(d.getFullYear())
            const month = d.toLocaleString(lang, { month: "long" })
            const key = getTimeslotDateKey(slot)

            years[year] = years[year] || {}
            years[year][month] = years[year][month] || {}
            years[year][month][key] = years[year][month][key] || []
            years[year][month][key].push(slot)
        })

        const result: YearGroup[] = []
        Object.entries(years).forEach(([year, monthsMap]) => {
            const months: MonthGroup[] = []
            Object.entries(monthsMap).forEach(([month, daysMap]) => {
                const days: MonthGroup["days"] = Object.entries(daysMap).map(
                    ([key, slots]) => {
                        // Use first slot for labels
                        const first = slots[0]
                        const fd = new Date(first.start_slot)
                        const ed = new Date(first.end_slot)
                        const labelDay = fd.toLocaleString(lang, {
                            weekday: "long",
                        })
                        const startLabel = `${fd.getDate()} ${month}`
                        // If end time is before noon, it's an overnight spillover —
                        // treat effective end as the previous day
                        const effectiveEnd = new Date(ed)
                        if (effectiveEnd.getHours() < 12) {
                            effectiveEnd.setDate(effectiveEnd.getDate() - 1)
                        }
                        const startDay = new Date(fd.getFullYear(), fd.getMonth(), fd.getDate())
                        const effectiveEndDay = new Date(effectiveEnd.getFullYear(), effectiveEnd.getMonth(), effectiveEnd.getDate())
                        const isSameDay = startDay.getTime() === effectiveEndDay.getTime()
                        const endLabel = `${effectiveEnd.getDate()} ${effectiveEnd.toLocaleString(lang, { month: "long" })}`
                        const labelDate = isSameDay
                            ? startLabel
                            : `${startLabel} - ${endLabel}`

                        // Day sold out if all slots disabled
                        const isSoldOut = slots.every((s) =>
                            isTimeslotDisabled(s)
                        )

                        // Day has waitlist if any type has wait_list > 0 and greater than max
                        const hasWaitlist = slots.some((s) =>
                            s.types.some((type) => {
                                const ticketDetails =
                                    event.availableTickets.find(
                                        (t) => t._id === type.type_id
                                    )
                                const max =
                                    typeof ticketDetails?.max === "number"
                                        ? ticketDetails.max
                                        : 0
                                const wl =
                                    typeof type.wait_list === "number"
                                        ? type.wait_list
                                        : 0
                                return wl > 0 && (max === 0 || wl > max)
                            })
                        )

                        return {
                            key,
                            labelDay,
                            labelDate,
                            slots,
                            isSoldOut,
                            hasWaitlist,
                        }
                    }
                )
                months.push({ month, days })
            })
            result.push({ year, months })
        })

        return result
    }, [tickets, event?.availableTickets, lang])

    if (!tickets || tickets.length === 0) {
        return <StepPlaceholderMessage message={t("no_available_days")} />
    }

    return (
        <FadeOutEffect fadeStartAt={700} fadeHeight={100}>
            <div className="w-full mt-3">
                {groupedDays.map((yearGroup) => (
                    <div key={yearGroup.year}>
                        <h6 className="text-lg lg:text-xl font-light ">
                            {yearGroup.year}
                        </h6>
                        {yearGroup.months.map((monthGroup) => (
                            <div
                                className="flex flex-col gap-y-1 mb-5"
                                key={monthGroup.month}
                            >
                                <h6 className="text-lg lg:text-xl  font-bold">
                                    {t(monthGroup.month)}
                                </h6>
                                {monthGroup.days.map((day, index) => {
                                    const isSelected =
                                        selectedDayKey === day.key
                                    return (
                                        <SelectionCard
                                            variant="day"
                                            key={day.key}
                                            day={getTruncatedName(day.labelDay)}
                                            date={day.labelDate}
                                            isFirst={index === 0}
                                            isLast={
                                                index ===
                                                monthGroup.days.length - 1
                                            }
                                            onClick={() =>
                                                handleDayClick(
                                                    day.key,
                                                    day.slots
                                                )
                                            }
                                            isSelected={isSelected}
                                            isSoldOut={day.isSoldOut}
                                            // hasWaitlist={day.hasWaitlist}
                                        />
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </FadeOutEffect>
    )
}

export default memo(SelectDay)
